import { CSSProperties, useState } from 'react';

import { CalendarOutlined } from '@ant-design/icons';
import { Button, Popover, theme } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';

import DateRangeCalendar from './DateRangeCalendar';
import {
  DateRangePreset,
  DateRangeValue,
  RangeConstraints,
  VALUE_FORMAT,
  currentWeekPreset,
  findMatchingPreset,
  formatRangeLabel,
  isRangeAllowed,
  previousWeekPreset,
  resolvePresetRange,
  resolveToday,
  todayPreset,
  yesterdayPreset,
} from './utils';

import './DateRangePresetPicker.scss';

const I18N_BASE = 'components.date_range_preset_picker';

export const DEFAULT_PRESETS: DateRangePreset[] = [
  todayPreset(),
  yesterdayPreset(),
  currentWeekPreset(),
  previousWeekPreset(),
];

export interface DateRangePresetPickerProps extends RangeConstraints {
  value: DateRangeValue;
  onChange: (range: DateRangeValue) => void;
  presets?: DateRangePreset[];
  /** Zone in which "today" is computed; the local one when omitted. */
  timeZone?: string;
  disabled?: boolean;
  className?: string;
  /** Reference instant for "today" (defaults to the current time). */
  now?: Dayjs;
}

/**
 * Button that shows the active range and opens a list of suggested ranges plus
 * a "custom range" entry with an inline calendar. Presets apply immediately;
 * the custom range applies from its own button.
 */
function DateRangePresetPicker({
  value,
  onChange,
  presets = DEFAULT_PRESETS,
  timeZone,
  disabled = false,
  className,
  now,
  disabledDate,
  maxDays,
}: DateRangePresetPickerProps) {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const [open, setOpen] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);

  const today = resolveToday(now ?? dayjs(), timeZone);
  const constraints = { disabledDate, maxDays };
  const matching = findMatchingPreset(value, presets, today);
  const customActive = customOpen || !matching;

  const translate = (preset: DateRangePreset) =>
    t(preset.label, { defaultValue: preset.label, ...preset.labelParams });

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (next) setCustomOpen(false);
  };

  const apply = (range: DateRangeValue) => {
    onChange(range);
    setOpen(false);
  };

  // The popover is portaled to document.body, so antd's context does not
  // reach it through CSS: the variables are published on its own root.
  const cssVars = {
    '--drp-primary': token.colorPrimary,
    '--drp-primary-bg': token.colorPrimaryBg,
    '--drp-on-primary': token.colorTextLightSolid,
    '--drp-text': token.colorText,
    '--drp-text-muted': token.colorTextTertiary,
    '--drp-text-disabled': token.colorTextDisabled,
    '--drp-border': token.colorBorderSecondary,
    '--drp-hover': token.colorFillTertiary,
    '--drp-radius': `${token.borderRadius}px`,
  } as CSSProperties;

  const content = (
    <div className="date-range-preset-picker__content" style={cssVars}>
      <ul className="drp-list">
        {presets.map((preset) => {
          const range = resolvePresetRange(preset, today);
          const unavailable = !range || !isRangeAllowed(range, constraints);

          return (
            <li key={preset.key}>
              <button
                type="button"
                className="drp-item"
                aria-pressed={!customOpen && matching?.key === preset.key}
                disabled={unavailable}
                onClick={() => range && apply(range)}
              >
                {translate(preset)}
              </button>
            </li>
          );
        })}
        <li>
          <button
            type="button"
            className="drp-item"
            aria-pressed={customActive}
            onClick={() => setCustomOpen(true)}
          >
            {t(`${I18N_BASE}.custom_range`)}
          </button>
        </li>
      </ul>

      {customOpen && (
        <DateRangeCalendar
          value={value}
          today={today.format(VALUE_FORMAT)}
          disabledDate={disabledDate}
          maxDays={maxDays}
          onApply={apply}
        />
      )}
    </div>
  );

  return (
    <Popover
      open={open}
      onOpenChange={handleOpenChange}
      trigger="click"
      placement="bottomLeft"
      arrow={false}
      content={content}
      overlayClassName="date-range-preset-picker__popover"
    >
      <Button
        className={className}
        icon={<CalendarOutlined />}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        {matching ? translate(matching) : formatRangeLabel(value)}
      </Button>
    </Popover>
  );
}

export default DateRangePresetPicker;
